import React, { useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";
import HandleField from "@/components/HandleField";
import Avatar from "@/components/Avatar";
import { toast } from "sonner";
import { Camera, Trash2 } from "lucide-react";

export default function Account() {
  const { user, reload } = useAuth();
  const [handle, setHandle] = useState(user?.handle || "");
  const [saving, setSaving] = useState(false);
  const [avatar, setAvatar] = useState(user?.avatar || "");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/handle", { handle: handle.trim() });
      toast.success(handle.trim() ? `Others will now see you as “${data.handle}”.` : "Handle removed — your name will show again.");
      reload?.();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not save your handle");
    } finally {
      setSaving(false);
    }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/uploads", fd);
      const { data } = await api.put("/me/avatar", { path: up.path });
      setAvatar(data.avatar);
      toast.success("Profile photo updated.");
      reload?.();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not upload your photo");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAvatar = async () => {
    setUploading(true);
    try {
      await api.put("/me/avatar", { path: "" });
      setAvatar("");
      toast.success("Profile photo removed.");
      reload?.();
    } catch {
      toast.error("Could not remove your photo");
    } finally {
      setUploading(false);
    }
  };

  const displayNow = (user?.handle || "").trim() || user?.name || "—";

  return (
    <div data-testid="account-page" className="max-w-[640px] mx-auto px-6 py-16">
      <div className="label-eyebrow">Your account</div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">Profile &amp; privacy.</h1>

      <div className="hairline mt-8 p-7 space-y-7">
        {/* Profile photo */}
        <div className="flex items-center gap-5">
          <Avatar src={avatar} name={displayNow} size={72} testid="account-avatar" />
          <div>
            <div className="text-sm text-neutral-500">Signed in as</div>
            <div className="font-medium" data-testid="account-email">{user?.email}</div>
            <div className="mt-2 flex items-center gap-3">
              <input ref={fileRef} type="file" accept="image/*" onChange={onPickFile} className="hidden" data-testid="account-avatar-input" />
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="account-avatar-upload"
                className="btn-outline text-[0.68rem] inline-flex items-center gap-1.5">
                <Camera size={13} /> {uploading ? "Uploading…" : (avatar ? "Change photo" : "Add photo")}
              </button>
              {avatar && (
                <button onClick={removeAvatar} disabled={uploading} data-testid="account-avatar-remove"
                  className="text-[0.68rem] text-neutral-500 hover:text-red-600 inline-flex items-center gap-1.5">
                  <Trash2 size={13} /> Remove
                </button>
              )}
            </div>
            <p className="mt-1.5 text-xs text-neutral-400">Shown next to your handle on your storefront and reviews.</p>
          </div>
        </div>

        {/* Handle */}
        <div className="hairline-t pt-6">
          <label className="label-eyebrow">Public handle</label>
          <p className="mt-1.5 text-sm text-neutral-600">
            This is the name other buyers and sellers see across the marketplace — on your listings,
            storefront, offers, trades and reviews. Your real name stays private. You can use spaces,
            dashes and underscores. Leave blank to show your name.
          </p>
          <div className="mt-3">
            <HandleField value={handle} onChange={setHandle} testidPrefix="account-handle" />
          </div>
          <div className="mt-3 flex items-center gap-3">
            <span className="text-xs text-neutral-500">Others currently see: <strong data-testid="account-current-display">{displayNow}</strong></span>
          </div>
          <button onClick={save} disabled={saving} data-testid="account-save-handle" className="btn-primary mt-5">
            {saving ? "Saving…" : "Save handle"}
          </button>
        </div>
      </div>
    </div>
  );
}
